/**
 * The Building Session's Save App toasts, one definition for both windows
 * (the reworked Build window and the legacy one). A save is ordinarily a
 * re-save of an already-live app, so the copy says who can open it, from the
 * server-derived audience (FRONT-1879), falling back to the smallest audience
 * when the field is missing so the toast never throws. A save that published
 * through Git also offers Undo for that publish (FRONT-3368).
 */
import { toast } from 'sonner';
import type { QueryClient } from '@tanstack/react-query';
import { APP_AUDIENCE_COPY } from './coding-events';
import { openAppUrl } from '~/lib/open-app-url';
import { publishUndoToast } from '~/features/apps/publish-undo';
import type { PublishCodingSessionResult } from '~/hooks/mutations/use-coding-mutations';

export function announceSavedApp(qc: QueryClient, result: PublishCodingSessionResult): void {
  const audienceHint = (APP_AUDIENCE_COPY[result.audience] ?? APP_AUDIENCE_COPY['only-you']).hint;
  toast.success(result.updated ? 'App updated' : 'Saved to Apps', {
    description: result.updated ? `“${result.title}” now holds your latest changes. ${audienceHint}` : `“${result.title}” is in your Apps. ${audienceHint}`,
    action: { label: 'Open app', onClick: () => openAppUrl(result.url) },
    duration: 8000,
    ...publishUndoToast(qc, result.slug, result, 'cancel'),
  });
  // Informational: two files were kept out on purpose, the app published fine (FRONT-2027).
  if (result.warnings.length > 0) toast.info(result.warnings[0], { duration: 8000 });
}
